import { useEffect, useRef, useState, useCallback } from "react";
import type {
  LobbyState,
  TdcStateUpdate,
  HandUpdatePayload,
  WelcomePayload,
  RoundHistoryItem,
} from "@shared/events";
import type { Card } from "@shared/engine-types";
import type { GamePhase } from "@shared/phase";
import { createSocket, type TypedSocket } from "./socket";
import { sfx, unlockAudio } from "./audio";

export interface GameSession {
  gameCode: string;
  participantId: string;
  role: "TDC" | "PHC";
  token: string;
}

export interface GameStoreState {
  session: GameSession | null;
  socketStatus: "idle" | "connecting" | "connected" | "disconnected" | "error";
  welcome: WelcomePayload | null;
  lobby: LobbyState | null;
  tdcView: TdcStateUpdate | null;
  hand: Card[];
  rejectMessage: string | null;
  error: string | null;
  countdownSeconds: number | null;
  unoChallenge: { challengedSeatIndex: number; deadlineMs: number } | null;
  history: RoundHistoryItem[];
  ended: { reason: string; history: RoundHistoryItem[] } | null;
  // Transient: last broadcast card_played, used by TDC for fly-from-seat animation.
  lastPlayed: { seatIndex: number; cardId: string; value: string; at: number } | null;
  // Transient: last broadcast card_drawn, used by TDC for fly-to-seat animation
  // and the "drew a card" announcement banner.
  lastDrawn: { seatIndex: number; count: number; at: number } | null;
}

export type Emit = TypedSocket["emit"];

export interface GameStore extends GameStoreState {
  setSession: (s: GameSession | null) => void;
  emit: Emit;
  resetEphemeral: () => void;
}

export function useGameStore(session: GameSession | null): GameStore {
  const [state, setState] = useState<GameStoreState>({
    session,
    socketStatus: "idle",
    welcome: null,
    lobby: null,
    tdcView: null,
    hand: [],
    rejectMessage: null,
    error: null,
    countdownSeconds: null,
    unoChallenge: null,
    history: [],
    ended: null,
    lastPlayed: null,
    lastDrawn: null,
  });

  const socketRef = useRef<TypedSocket | null>(null);
  const sessionRef = useRef<GameSession | null>(session);
  sessionRef.current = session;

  useEffect(() => {
    if (!session) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setState((s) => ({
        ...s,
        session: null,
        socketStatus: "idle",
        welcome: null,
        lobby: null,
        tdcView: null,
        hand: [],
      }));
      return;
    }
    setState((s) => ({ ...s, session, socketStatus: "connecting", error: null }));

    const sock = createSocket({ token: session.token });
    socketRef.current = sock;

    sock.on("connect", () => setState((s) => ({ ...s, socketStatus: "connected" })));
    sock.on("disconnect", () => setState((s) => ({ ...s, socketStatus: "disconnected" })));
    sock.on("connect_error", (err) =>
      setState((s) => ({ ...s, socketStatus: "error", error: err.message })),
    );

    sock.on("welcome", (payload) => {
      setState((s) => ({
        ...s,
        welcome: payload,
        lobby: payload.lobby,
        tdcView: payload.tdcView,
        history: payload.tdcView?.roundHistory ?? s.history,
      }));
    });
    sock.on("lobby_update", (payload) => setState((s) => ({ ...s, lobby: payload })));
    sock.on("game_state_update", (payload) => {
      setState((s) => ({
        ...s,
        tdcView: payload,
        history: payload.roundHistory,
        unoChallenge: payload.unoChallenge,
      }));
    });
    sock.on("hand_update", (payload: HandUpdatePayload) => {
      setState((s) => ({ ...s, hand: payload.cards }));
    });
    sock.on("phase_changed", ({ phase }: { phase: GamePhase }) => {
      setState((s) => (s.tdcView ? { ...s, tdcView: { ...s.tdcView, phase } } : s));
    });
    sock.on("countdown", ({ secondsRemaining }) =>
      setState((s) => ({ ...s, countdownSeconds: secondsRemaining })),
    );
    sock.on("first_player_selected", () => {
      sfx.turnChime();
    });
    sock.on("deal_complete", () => {
      sfx.cardPlay();
    });
    sock.on("card_played", (p) => {
      sfx.cardPlay();
      setState((s) => ({
        ...s,
        rejectMessage: null,
        lastPlayed: {
          seatIndex: p.seatIndex,
          cardId: p.card.id,
          value: p.card.value,
          at: Date.now(),
        },
      }));
    });
    sock.on("card_drawn", (p) => {
      sfx.draw();
      setState((s) => ({
        ...s,
        lastDrawn: { seatIndex: p.seatIndex, count: p.count, at: Date.now() },
      }));
    });
    sock.on("uno_declared", () => sfx.turnChime());
    sock.on("uno_challenge", (payload) =>
      setState((s) => ({ ...s, unoChallenge: payload })),
    );
    sock.on("uno_challenge_resolved", () => setState((s) => ({ ...s, unoChallenge: null })));
    sock.on("round_ended", () => {
      sfx.winnerFanfare();
    });
    sock.on("game_ended", (payload) => {
      setState((s) => ({ ...s, ended: payload }));
    });
    sock.on("kicked", () => {
      // Server kicked us — clear local session for this game and bounce home.
      try {
        if (session?.gameCode) {
          localStorage.removeItem(`unophone:token:${session.gameCode}`);
        }
        localStorage.removeItem("unophone:last_code");
      } catch {
        // ignore storage errors
      }
      window.location.href = "/";
    });
    sock.on("action_rejected", ({ reason }) => {
      sfx.invalid();
      setState((s) => ({ ...s, rejectMessage: reason }));
      setTimeout(() => {
        setState((s) => (s.rejectMessage === reason ? { ...s, rejectMessage: null } : s));
      }, 2500);
    });

    // Forced re-sync on tab visibility (RC-04).
    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      if (!sock.connected) sock.connect();
      else sock.emit("state_resync");
    };
    document.addEventListener("visibilitychange", onVisible);

    sock.connect();

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      sock.removeAllListeners();
      sock.disconnect();
    };
  }, [session?.token]);

  const setSession = useCallback((s: GameSession | null) => {
    setState((prev) => ({ ...prev, session: s }));
  }, []);

  const emit = useCallback<Emit>((...args) => {
    unlockAudio();
    if (!socketRef.current) return false as unknown as ReturnType<Emit>;
    return (socketRef.current.emit as Emit)(...args);
  }, []);

  const resetEphemeral = useCallback(() => {
    setState((s) => ({ ...s, rejectMessage: null, ended: null }));
  }, []);

  return { ...state, setSession, emit, resetEphemeral };
}
